import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import testScriptsService, { UpdateTestScriptDto } from '../api/testScriptsService';
import type { TestScript, TestScriptPurpose } from '../types/types';

export const useTestScripts = (purpose?: TestScriptPurpose) => {
    return useQuery<TestScript[]>({
        queryKey: purpose ? ['testScripts', purpose] : ['testScripts'],
        queryFn: () => testScriptsService.getAll(purpose),
        refetchOnWindowFocus: false,
    });
};

export const useTestScript = (id: number | undefined) => {
    return useQuery<TestScript>({
        queryKey: ['testScript', id],
        queryFn: () => testScriptsService.getById(id!),
        enabled: id != null,
        refetchOnWindowFocus: false,
    });
};

export const useCreateTestScript = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ name, purpose, file }: { name: string; purpose: TestScriptPurpose; file: File }) =>
            testScriptsService.create(name, purpose, file),
        onSuccess: (_, { purpose }) => {
            qc.invalidateQueries({ queryKey: ['testScripts'] });
            qc.invalidateQueries({ queryKey: ['testScripts', purpose] });
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
        },
    });
};

export const useUpdateTestScript = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateTestScriptDto }) =>
            testScriptsService.update(id, dto),
        onSuccess: (script, { id }) => {
            qc.invalidateQueries({ queryKey: ['testScripts'] });
            qc.invalidateQueries({ queryKey: ['testScripts', script.purpose] });
            qc.invalidateQueries({ queryKey: ['testScript', id] });
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
        },
    });
};

export const useReplaceTestScriptFile = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ script, file }: { script: TestScript; file: File }) =>
            testScriptsService.replaceFile(script.id, file),
        onSuccess: (updatedScript, { script }) => {
            qc.invalidateQueries({ queryKey: ['testScripts'] });
            qc.invalidateQueries({ queryKey: ['testScripts', updatedScript.purpose] });
            qc.invalidateQueries({ queryKey: ['testScript', script.id] });
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
        },
    });
};

export const useDeleteTestScript = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (script: TestScript) => testScriptsService.remove(script.id),
        onSuccess: (_, script) => {
            qc.invalidateQueries({ queryKey: ['testScripts'] });
            qc.invalidateQueries({ queryKey: ['testScripts', script.purpose] });
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
        },
    });
};
